<script setup lang="ts">
import moment from "moment";
import { computed, onUnmounted, ref, toRef, watch } from "vue";

import { getPropertyConfig, getPropertyDisplayTitle } from "~/common/properties";
import AiSpinner from "~/components/dumb/AiSpinner.vue";
import Progressbar from "~/components/dumb/ProgressBar.vue";
import Template from "~/components/dumb/Template.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { COMPLETED_STATUS_KINDS } from "~/components/visualization/constants";
import { ArrowRightIcon, DatesFieldIcon, DueDateFieldIcon, XIcon } from "~/icons";
import { EditorMode, Placement } from "~/shared/enums";
import type { PropertyAnyDates, PropertyValueForKind, Recurrence, Task } from "~/shared/types";
import { useAppStore, useDataStore } from "~/stores";
import { getMsUntilNext, getRelativeTimeForDatesDate } from "~/utils/time";

const props = defineProps<{
  task?: Task;
  property: PropertyAnyDates;
  value: PropertyValueForKind<PropertyAnyDates>;
  statusDuid: string | null;
  recurrence: Recurrence | null;
  loading?: boolean;
  showIfEmpty?: boolean;
  showIcon?: boolean;
  editorMode: EditorMode;
}>();

const emit = defineEmits<{
  accept: [];
  reject: [];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();

const subtasks = computed(() =>
  props.task
    ? dataStore.getTasksByDuidsOrderedIfLoaded(dataStore.getDescendantDuidsUiOnly(props.task), {
        includeTrashed: false,
        includeDraft: false,
      })
    : []
);

const startAt = toRef(() => props.value?.[0]);
const dueAt = toRef(() => props.value?.[1]);

const earliestSubtaskStartAt = computed(() => {
  if (!("adtl" in props.property) || !props.property.adtl.rollup || !props.task) {
    return null;
  }
  const value = props.task.rollup[props.property.duid];
  if (!Array.isArray(value) || value === null) {
    return null;
  }

  return value[0] ?? null;
});

const latestSubtaskDueAt = computed(() => {
  if (!("adtl" in props.property) || !props.property.adtl.rollup || !props.task) {
    return null;
  }
  const value = props.task.rollup[props.property.duid];
  if (!Array.isArray(value) || value === null) {
    return null;
  }

  return value[1] ?? null;
});

const rolledUpStartAt = computed(() => startAt.value ?? earliestSubtaskStartAt.value);
const rolledUpDueAt = computed(() => dueAt.value ?? latestSubtaskDueAt.value);

const propertyConfig = computed(() => getPropertyConfig(props.property.kind));
const isRange = computed(
  () =>
    ("adtl" in props.property && props.property.adtl.isRange) ||
    propertyConfig.value.alwaysShowForLayouts?.includes(appStore.layoutKind)
);
const showDuration = computed(
  () =>
    ("adtl" in props.property && props.property.adtl.showDuration) ||
    propertyConfig.value.alwaysShowForLayouts?.includes(appStore.layoutKind)
);
const hasValue = computed(() => (isRange.value && rolledUpStartAt.value) || rolledUpDueAt.value);

const getValues = () => ({
  startAtStr: rolledUpStartAt.value ? getRelativeTimeForDatesDate(rolledUpStartAt.value) : null,
  dueAtStr: rolledUpDueAt.value ? getRelativeTimeForDatesDate(rolledUpDueAt.value) : null,
  duration:
    rolledUpStartAt.value && rolledUpDueAt.value && rolledUpStartAt.value !== rolledUpDueAt.value
      ? moment(rolledUpStartAt.value).to(moment(rolledUpDueAt.value), true)
      : null,
  isActive:
    isRange.value && !!rolledUpStartAt.value
      ? moment(rolledUpStartAt.value).isSameOrBefore(moment(), "day") &&
        (!rolledUpDueAt.value || moment(rolledUpDueAt.value).isSameOrAfter(moment(), "day"))
      : !!rolledUpDueAt.value && moment(rolledUpDueAt.value).isSame(moment(), "day"),
  isOverdue: !!rolledUpDueAt.value && moment(rolledUpDueAt.value).isBefore(moment(), "day"),
});

const now = ref(new Date());
const values = ref<ReturnType<typeof getValues>>({
  startAtStr: null,
  dueAtStr: null,
  duration: null,
  isActive: false,
  isOverdue: false,
});

const progressPctg = computed(() => {
  if (!rolledUpStartAt.value || !rolledUpDueAt.value || moment(now.value).isBefore(moment(startAt.value))) {
    return null;
  }
  const start = moment(rolledUpStartAt.value);
  const total = moment(rolledUpDueAt.value).diff(start);
  const progress = moment(now.value).diff(start);
  return (progress / total) * 100;
});

const isChipRecommendationMode = computed(() => props.editorMode === EditorMode.CHIP_RECOMMENDATION);
const isChipBaseMode = computed(() => props.editorMode === EditorMode.CHIP);
const isChipMode = computed(() => isChipBaseMode.value || isChipRecommendationMode.value);
const isListMiniRoadmapMode = computed(() => props.editorMode === EditorMode.LIST_MINI_ROADMAP);
const isListMode = computed(
  () =>
    props.editorMode === EditorMode.LIST ||
    props.editorMode === EditorMode.LIST_MINI ||
    props.editorMode === EditorMode.LIST_MINI_TCM ||
    isListMiniRoadmapMode.value
);
const isBoardMode = computed(() => props.editorMode === EditorMode.BOARD);
const isDetailBaseMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isDetailRecommendationMode = computed(() => props.editorMode === EditorMode.DETAIL_RECOMMENDATION);
const isTaskDetailMode = computed(() => isDetailBaseMode.value || isDetailRecommendationMode.value);
const isRecommendationMode = computed(() => isChipRecommendationMode.value || isDetailRecommendationMode.value);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(
  () => props.editorMode === EditorMode.FORM || props.editorMode === EditorMode.AUTOMATION
);

const isPropertyReadOnly = computed(() => getPropertyConfig(props.property.kind).readOnly);

const isChipModeWithProgressBar = computed(() => isChipMode.value && progressPctg.value !== null);
const isTaskDetailModeWithProgressBar = computed(
  () => isTaskDetailMode.value && isRange.value && progressPctg.value !== null
);

const isCompletedStatus = computed(() => {
  const statusKind = props.statusDuid ? dataStore.getStatusByDuid(props.statusDuid)?.kind : undefined;
  if (!statusKind) {
    return false;
  }
  return COMPLETED_STATUS_KINDS.has(statusKind);
});

let timeout: ReturnType<typeof setTimeout> | undefined;

const resetValuesAndTimeout = () => {
  now.value = new Date();
  values.value = getValues();
  clearTimeout(timeout);
  if (!rolledUpStartAt.value && rolledUpDueAt.value) {
    return;
  }
  // oxlint-disable-next-line dart/no-set-timeout
  timeout = setTimeout(resetValuesAndTimeout, getMsUntilNext("hour"));
};

const hoverStyle = computed(() => {
  if (
    (isDetailBaseMode.value || isFormOrAutomationMode.value) &&
    !isPropertyReadOnly.value &&
    !isTaskDetailModeWithProgressBar.value
  ) {
    return "hover:bg-lt";
  }
  if (isDetailRecommendationMode.value) {
    return "hover:bg-recommendation-base/20";
  }
  if (isChipBaseMode.value) {
    return "hover:bg-md";
  }
  if (isChipRecommendationMode.value) {
    return "hover:bg-recommendation-base/40 dark:hover:bg-recommendation-base/40";
  }
  return "";
});

const textStyle = computed(() => {
  if (!isPropertyReadOnly.value) {
    if (values.value.isActive && !isCompletedStatus.value) {
      return "text-primary-base dark:text-primary-base";
    }
    if (values.value.isOverdue && !isCompletedStatus.value) {
      return "text-danger-base dark:text-danger-base";
    }
  }
  return "text-md";
});

const borderStyle = computed(() => {
  if (isChipRecommendationMode.value) {
    return "border-recommendation-base/50 bg-recommendation-base/30";
  }
  if (isChipBaseMode.value) {
    return "border-oncolor";
  }
  if (isListMode.value || isTaskDetailMode.value) {
    return "border-none";
  }
  if (!isPropertyReadOnly.value) {
    if (values.value.isActive && !isCompletedStatus.value) {
      return "border-primary-base/50 dark:border-primary-base/50";
    }
    if (values.value.isOverdue && !isCompletedStatus.value) {
      return "border-danger-base/50 dark:border-danger-base/50";
    }
  }
  return "border-oncolor";
});

const iconStyle = computed(() => {
  if (!isPropertyReadOnly.value) {
    if (values.value.isActive && !isCompletedStatus.value) {
      return "text-primary-base";
    }
    if (values.value.isOverdue && !isCompletedStatus.value) {
      return "text-danger-base";
    }
  }
  if (isChipRecommendationMode.value) {
    return "text-recommendation-base";
  }
  if (isChipMode.value && (startAt.value || dueAt.value)) {
    return "text-primary-base";
  }
  return "text-md";
});

const hasSubtasks = computed(() => subtasks.value.length > 0);

const selectedDateRange = computed(() => ({
  start:
    startAt.value && earliestSubtaskStartAt.value && !moment(startAt.value).isSame(earliestSubtaskStartAt.value)
      ? startAt.value
      : null,
  end:
    dueAt.value && latestSubtaskDueAt.value && !moment(dueAt.value).isSame(latestSubtaskDueAt.value)
      ? dueAt.value
      : null,
}));

const tooltipText = computed(() => {
  if (!hasSubtasks.value || !("adtl" in props.property) || !props.property.adtl?.rollup) {
    return "";
  }

  const { start, end } = selectedDateRange.value;
  if (!start && !end) {
    return "";
  }

  if (isRange.value && start && end) {
    return "The parent dates are overriding the subtask dates";
  }
  if (isRange.value && start) {
    return "The parent start date is overriding the subtask start date";
  }
  return "The parent due date is overriding the subtask due date";
});

const rejectRecommendation = () => {
  emit("reject");
};

resetValuesAndTimeout();

watch([() => rolledUpStartAt.value, () => rolledUpDueAt.value], resetValuesAndTimeout);

onUnmounted(() => {
  clearTimeout(timeout);
});
</script>

<template>
  <component
    :is="isBoardMode ? Tooltip : Template"
    v-if="hasValue || showIfEmpty"
    :text="isBoardMode ? property?.title : undefined"
    class="max-w-full">
    <Tooltip :text="tooltipText" :placement="Placement.TOP" :disabled="!tooltipText.length" class="w-full">
      <button
        type="button"
        :class="[
          !isChipModeWithProgressBar && 'border',
          hoverStyle,
          !isChipModeWithProgressBar && borderStyle,
          textStyle,
          isFormOrAutomationMode && '-ml-1 border-0 pl-0.5 text-sm',
          isChipMode && 'h-[26px] gap-1 text-sm',
          isChipMode && progressPctg === null && 'px-1 py-0.5',
          isBoardMode && 'gap-1 px-1',
          !isChipMode && !isBoardMode && 'gap-2',
          !isChipMode && !isTaskDetailMode && !isFormOrAutomationMode && 'h-5 truncate text-xs',
          isTaskDetailMode && 'w-full justify-start text-left',
          isTaskDetailMode && (!isRange || progressPctg === null ? 'px-2' : 'group/dates-with-progress'),
          isPropertyReadOnly && !isBoardMode && 'cursor-default',
          !isTaskDetailMode && 'justify-center',
          isDetailRecommendationMode && 'bg-recommendation-base/10',
        ]"
        class="flex items-center rounded-sm focus-ring-none"
        tabindex="-1">
        <div v-if="loading" class="flex size-full items-center justify-center px-4 py-0.5">
          <AiSpinner class="icon-sm" />
        </div>
        <span class="sr-only">{{ getPropertyDisplayTitle(property) }}</span>
        <component
          :is="isRange ? DatesFieldIcon : isPropertyReadOnly ? propertyConfig.icon : DueDateFieldIcon"
          v-if="
            !loading &&
            (showIcon || isChipMode || (isFormOrAutomationMode && hasValue) || (isTaskDetailMode && hasValue)) &&
            (isBoardMode || !isRange || progressPctg === null)
          "
          :class="[iconStyle, isChipMode ? 'icon-sm' : 'icon-xs']"
          class="shrink-0" />
        <div
          v-if="!loading && !isBoardMode && isRange && progressPctg !== null"
          class="absolute inset-0 flex items-center"
          :class="{
            'mx-2': isTaskDetailMode,
            'px-2 py-1.5': !isTaskDetailMode && !isChipModeWithProgressBar,
          }">
          <Progressbar :value="progressPctg" />
        </div>
        <div
          v-if="!loading && isTaskDetailModeWithProgressBar"
          class="pointer-events-none absolute inset-0 mx-2 flex items-center opacity-0 transition-opacity group-hover/dates-with-progress:opacity-100">
          <div class="size-full overflow-hidden rounded-sm border border-primary-base" />
        </div>
        <div
          v-if="!loading"
          class="relative w-full select-none"
          :class="[
            isBoardMode || isListMode ? 'truncate' : 'break-words hyphens-auto',
            !isBoardMode && isRange && progressPctg !== null && (isChipMode ? 'px-2 text-md' : 'px-1.5 text-md'),
            !loading && !isBoardMode && isRange && progressPctg !== null && !isTaskDetailMode && !isChipMode && 'py-1',
            isTaskDetailMode && 'flex min-h-7 items-center',
            isTaskDetailModeWithProgressBar && 'mx-2',
          ]">
          <div v-if="hasValue" :class="isListMode && 'truncate'">
            <span v-if="isRange && values.startAtStr" class="align-middle">{{ values.startAtStr }}</span>
            <ArrowRightIcon
              v-if="isRange && (values.startAtStr || values.dueAtStr)"
              class="inline-block size-3 align-middle"
              :class="{ 'ml-1': isRange && values.startAtStr }" />
            <span
              v-if="values.dueAtStr"
              class="align-middle"
              :class="{ 'ml-1': isRange && (values.startAtStr || values.dueAtStr) }">
              {{ values.dueAtStr }}
            </span>
            <span
              v-if="((isRange && showDuration) || isListMiniRoadmapMode) && values.duration"
              class="ml-1 align-middle">
              ({{ values.duration }})
            </span>
          </div>
          <span v-else :class="(isTaskDetailMode || isFormOrAutomationMode) && 'text-vlt'">
            {{
              isChipMode
                ? getPropertyDisplayTitle(property)
                : isAutomationMode
                  ? "(Unset)"
                  : isFormOrAutomationMode
                    ? "Set date"
                    : "None"
            }}
          </span>
        </div>
        <Tooltip v-if="isRecommendationMode" text="Clear AI recommendation">
          <span
            class="rounded-sm text-md hover:bg-recommendation-base/30 dark:hover:bg-recommendation-base/40"
            data-global-popper-ignore
            @click.stop="rejectRecommendation"
            @keydown.enter.stop="rejectRecommendation">
            <XIcon class="icon-xs" />
          </span>
        </Tooltip>
      </button>

      <div v-if="!showIfEmpty && !hasValue && loading" class="flex size-full items-center justify-center px-4 py-0.5">
        <AiSpinner class="icon-sm" />
      </div>
    </Tooltip>
  </component>
</template>
